import {
  type KeyboardEvent,
  type PointerEvent,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import {
  areaPath,
  bandTicks,
  type ChartColor,
  type ChartSeries,
  chartColorVar,
  evenTicks,
  linePath,
  nearestIndex,
  safeId,
} from "./chart-geometry";
import { cn } from "./cn";
import { niceTicks } from "./format";

/*
 * Pure SVG charts: no chart library, React 19 only. Marks wear the theme's
 * `--color-chart-N`; text never does. Height is fixed from the first paint
 * (width is measured), so a chart never shifts the layout. Every chart also
 * renders a visually hidden table of its values, and its tooltip is reachable
 * from the keyboard (arrows move, Escape hides, Enter selects on a bar chart).
 */

const FALLBACK_WIDTH = 640;
const TOP = 10;
const BOTTOM = 22;
const RIGHT = 12;
const BAR_MAX = 24;
const BAR_GAP = 2;

const axisText = "fill-muted-foreground type-figure text-2xs";
const focusOutline =
  "rounded-md outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring";

/** The wrapper's measured width; 640 where there is no ResizeObserver or no width. */
function useMeasuredWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const apply = (w: number) =>
      setWidth(w > 0 ? Math.round(w) : FALLBACK_WIDTH);
    apply(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (entry) apply(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

/** The index under the pointer or keyboard, and the keys that move it. */
function useActiveIndex(count: number, onEnter?: (index: number) => void) {
  const [active, setActive] = useState<number | null>(null);
  const onKeyDown = (event: KeyboardEvent) => {
    if (count === 0) return;
    const last = count - 1;
    switch (event.key) {
      case "ArrowRight":
        setActive(active === null ? 0 : Math.min(last, active + 1));
        break;
      case "ArrowLeft":
        setActive(active === null ? last : Math.max(0, active - 1));
        break;
      case "Home":
        setActive(0);
        break;
      case "End":
        setActive(last);
        break;
      case "Escape":
        setActive(null);
        return;
      case "Enter":
      case " ":
        if (active === null || !onEnter) return;
        onEnter(active);
        break;
      default:
        return;
    }
    event.preventDefault();
  };
  return { active, setActive, onKeyDown };
}

function gutterFor(labels: string[]): number {
  const longest = labels.reduce((m, l) => Math.max(m, l.length), 1);
  return Math.ceil(longest * 6.4 + 12);
}

type TooltipRow = {
  key: string;
  label: string;
  color: ChartColor;
  text: string;
};

/** One readout for every series at an index; clamped inside the chart box. */
function ChartTooltip({
  anchorX,
  width,
  title,
  rows,
}: {
  anchorX: number;
  width: number;
  title: string;
  rows: TooltipRow[];
}) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const right = anchorX + 12;
    const left = right + w > width ? anchorX - 12 - w : right;
    el.style.left = `${Math.max(0, Math.min(left, width - w))}px`;
  }, [anchorX, width, title, rows]);
  return (
    <div
      ref={ref}
      role="tooltip"
      style={{ left: anchorX + 12 }}
      className="pointer-events-none absolute top-1 z-10 max-w-[calc(100%-8px)] min-w-32 rounded-md bg-popover px-3 py-2 text-xs text-popover-foreground shadow-drop"
    >
      <div className="mb-1 font-medium text-muted-foreground">{title}</div>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {rows.map((row) => (
          <li key={row.key} className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="h-0.5 w-3 shrink-0 rounded-full"
              style={{ background: chartColorVar(row.color) }}
            />
            <span className="type-figure font-semibold text-foreground">
              {row.text}
            </span>
            <span className="text-muted-foreground">{row.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Every value, for screen readers and as the way past the tooltip. */
function ValueTable({
  caption,
  labels,
  series,
  formatValue,
}: {
  caption: string;
  labels: string[];
  series: ChartSeries[];
  formatValue: (n: number) => string;
}) {
  return (
    <table className="sr-only">
      <caption>{caption}</caption>
      <thead>
        <tr>
          <th scope="col" />
          {series.map((s) => (
            <th key={s.key} scope="col">
              {s.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {labels.map((label, i) => (
          <tr key={`${i}-${label}`}>
            <th scope="row">{label}</th>
            {series.map((s) => {
              const v = s.values[i];
              return (
                <td key={s.key}>
                  {v === null || v === undefined ? "—" : formatValue(v)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function rowsAt(
  series: ChartSeries[],
  index: number,
  formatValue: (n: number) => string,
): TooltipRow[] {
  return series.map((s) => {
    const v = s.values[index];
    return {
      key: s.key,
      label: s.label,
      color: s.color,
      text: v === null || v === undefined ? "—" : formatValue(v),
    };
  });
}

/** Gridlines, the stronger zero baseline and the y labels. */
function YAxis({
  ticks,
  y,
  left,
  right,
  gutter,
  format,
}: {
  ticks: number[];
  y: (v: number) => number;
  left: number;
  right: number;
  gutter: number;
  format: (n: number) => string;
}) {
  // Ticks that round to the same text (an all-zero series' nominal domain
  // prints "$0" at every step) keep their gridline but lose the repeat label.
  const labels = ticks.map((t) => format(t));
  return (
    <g>
      {ticks.map((t, i) => (
        <g key={t}>
          <line
            x1={left}
            x2={right}
            y1={y(t)}
            y2={y(t)}
            strokeWidth={1}
            className={t === 0 ? "stroke-muted-foreground/40" : "stroke-border"}
          />
          <text
            x={gutter - 8}
            y={y(t)}
            textAnchor="end"
            dominantBaseline="central"
            className={axisText}
          >
            {labels.indexOf(labels[i] ?? "") === i ? labels[i] : null}
          </text>
        </g>
      ))}
    </g>
  );
}

function XLabels({
  indexes,
  labels,
  x,
  baseY,
  lastEnd,
}: {
  indexes: number[];
  labels: string[];
  x: (i: number) => number;
  baseY: number;
  lastEnd?: boolean;
}) {
  return (
    <g>
      {indexes.map((i) => (
        <text
          key={i}
          x={x(i)}
          y={baseY}
          textAnchor={
            lastEnd && i === labels.length - 1 && i > 0 ? "end" : "middle"
          }
          dominantBaseline="hanging"
          className={axisText}
        >
          {labels[i]}
        </text>
      ))}
    </g>
  );
}

type LineChartProps = {
  labels: string[];
  series: ChartSeries[];
  height?: number;
  formatValue: (n: number) => string;
  formatTick?: (n: number) => string;
  /**
   * Indexes that get an x label, or a picker given the point count and how
   * many labels the measured width holds (`dailyTicks` for a daily series);
   * about six evenly spaced by default.
   */
  xTicks?: number[] | ((count: number, maxLabels: number) => number[]);
  area?: boolean;
  ariaLabel: string;
  className?: string;
};

function LineChart({
  labels,
  series,
  height = 180,
  formatValue,
  formatTick,
  xTicks,
  area = false,
  ariaLabel,
  className,
}: LineChartProps) {
  const [ref, width] = useMeasuredWidth();
  const n = labels.length;
  const { active, setActive, onKeyDown } = useActiveIndex(n);

  const values = series.flatMap((s) =>
    s.values.filter((v): v is number => typeof v === "number"),
  );
  const ticks = niceTicks(
    values.length ? Math.min(...values) : 0,
    values.length ? Math.max(...values) : 0,
    3,
  );
  const domMin = ticks[0] ?? 0;
  const domMax = ticks[ticks.length - 1] ?? 1;
  const fmtTick = formatTick ?? formatValue;
  const left = gutterFor(ticks.map((t) => fmtTick(t)));
  const plotW = Math.max(1, width - left - RIGHT);
  const plotH = Math.max(1, height - TOP - BOTTOM);
  const maxLabels = Math.max(2, Math.floor(width / 84));
  const y = (v: number) => TOP + ((domMax - v) / (domMax - domMin)) * plotH;
  const x = (i: number) =>
    n <= 1 ? left + plotW / 2 : left + (i / (n - 1)) * plotW;
  const baseline = y(Math.min(domMax, Math.max(domMin, 0)));

  const pointsOf = (s: ChartSeries) =>
    labels.map((_, i) => {
      const v = s.values[i];
      return typeof v === "number" ? { x: x(i), y: y(v) } : null;
    });

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    setActive(nearestIndex(event.clientX - box.left, left, plotW, n, "point"));
  };

  return (
    <div
      ref={ref}
      className={cn("relative w-full min-w-0", className)}
      style={{ height }}
    >
      <div
        role="group"
        aria-label={ariaLabel}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onPointerMove={onPointerMove}
        onPointerLeave={() => setActive(null)}
        onBlur={() => setActive(null)}
        className={cn("h-full w-full touch-pan-y", focusOutline)}
      >
        <svg
          width="100%"
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          aria-hidden="true"
          focusable="false"
          // Absolute: an svg with a viewBox and a fixed height carries an aspect
          // ratio, which would size its column to the measured width and keep
          // a phone card from ever shrinking it.
          className="absolute inset-0 block overflow-visible"
          data-zero-y={baseline.toFixed(2)}
        >
          <YAxis
            ticks={ticks}
            y={y}
            left={left}
            right={left + plotW}
            gutter={left}
            format={fmtTick}
          />
          <XLabels
            indexes={
              typeof xTicks === "function"
                ? xTicks(n, maxLabels)
                : (xTicks ?? evenTicks(n, maxLabels))
            }
            labels={labels}
            x={x}
            baseY={height - BOTTOM + 8}
            lastEnd
          />
          {series.map((s) => (
            <g key={s.key}>
              {area ? (
                <path
                  data-mark="area"
                  d={areaPath(pointsOf(s), baseline)}
                  style={{ fill: chartColorVar(s.color) }}
                  fillOpacity={0.1}
                  stroke="none"
                />
              ) : null}
              <path
                data-mark="line"
                data-series={s.key}
                d={linePath(pointsOf(s))}
                fill="none"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ stroke: chartColorVar(s.color) }}
              />
            </g>
          ))}
          {active !== null ? (
            <g data-slot="crosshair">
              <line
                x1={x(active)}
                x2={x(active)}
                y1={TOP}
                y2={TOP + plotH}
                strokeWidth={1}
                className="stroke-muted-foreground/40"
              />
              {series.map((s) => {
                const v = s.values[active];
                return typeof v === "number" ? (
                  <circle
                    key={s.key}
                    cx={x(active)}
                    cy={y(v)}
                    r={4}
                    strokeWidth={2}
                    className="stroke-card"
                    style={{ fill: chartColorVar(s.color) }}
                  />
                ) : null;
              })}
            </g>
          ) : null}
        </svg>
      </div>
      {active !== null ? (
        <ChartTooltip
          anchorX={x(active)}
          width={width}
          title={labels[active] ?? ""}
          rows={rowsAt(series, active, formatValue)}
        />
      ) : null}
      <ValueTable
        caption={ariaLabel}
        labels={labels}
        series={series}
        formatValue={formatValue}
      />
    </div>
  );
}

type BarChartProps = {
  labels: string[];
  series: ChartSeries[];
  layout?: "grouped" | "stacked";
  height?: number;
  formatValue: (n: number) => string;
  formatTick?: (n: number) => string;
  highlightIndex?: number | null;
  onSelectIndex?: (index: number) => void;
  ariaLabel: string;
  className?: string;
};

type Bar = {
  key: string;
  seriesKey: string;
  color: ChartColor;
  index: number;
  x: number;
  y: number;
  w: number;
  h: number;
  sign: "positive" | "negative" | "zero";
};

function BarChart({
  labels,
  series,
  layout = "grouped",
  height = 200,
  formatValue,
  formatTick,
  highlightIndex = null,
  onSelectIndex,
  ariaLabel,
  className,
}: BarChartProps) {
  const [ref, width] = useMeasuredWidth();
  const gradientId = safeId("bars", useId());
  const n = labels.length;
  // Columns per band: every series in one stack when `stacked`, else one per
  // stack id, a series without one standing alone.
  const stackOf = (s: ChartSeries, si: number) =>
    layout === "stacked" ? "all" : (s.stack ?? `series-${si}`);
  const stackIds = [...new Set(series.map(stackOf))];
  const k = Math.max(1, stackIds.length);
  const stacked = k === 1 && series.length > 1;
  const { active, setActive, onKeyDown } = useActiveIndex(n, onSelectIndex);

  // Domain: one zero baseline, positives up and negatives down, each stack
  // summed on its own.
  let dataMin = 0;
  let dataMax = 0;
  for (let i = 0; i < n; i++) {
    const pos = new Map<string, number>();
    const neg = new Map<string, number>();
    series.forEach((s, si) => {
      const v = s.values[i];
      if (typeof v !== "number") return;
      const id = stackOf(s, si);
      if (v >= 0) pos.set(id, (pos.get(id) ?? 0) + v);
      else neg.set(id, (neg.get(id) ?? 0) + v);
    });
    for (const v of pos.values()) dataMax = Math.max(dataMax, v);
    for (const v of neg.values()) dataMin = Math.min(dataMin, v);
  }
  const ticks = niceTicks(dataMin, dataMax, 3);
  const domMin = ticks[0] ?? 0;
  const domMax = ticks[ticks.length - 1] ?? 1;
  const fmtTick = formatTick ?? formatValue;
  const left = gutterFor(ticks.map((t) => fmtTick(t)));
  const plotW = Math.max(1, width - left - RIGHT);
  const plotH = Math.max(1, height - TOP - BOTTOM);
  const y = (v: number) => TOP + ((domMax - v) / (domMax - domMin)) * plotH;
  const zeroY = y(0);
  const band = plotW / Math.max(1, n);
  const barW = stacked
    ? Math.max(1, Math.min(BAR_MAX, band * 0.6))
    : Math.max(1, Math.min(BAR_MAX, (band * 0.7 - BAR_GAP * (k - 1)) / k));
  const groupW = stacked ? barW : k * barW + (k - 1) * BAR_GAP;
  const bandX = (i: number) => left + i * band;

  const bars: Bar[] = [];
  for (let i = 0; i < n; i++) {
    const pos = new Map<string, number>();
    const neg = new Map<string, number>();
    series.forEach((s, si) => {
      const v = s.values[i];
      if (typeof v !== "number") return;
      const id = stackOf(s, si);
      const column = stackIds.indexOf(id);
      let from: number;
      let to: number;
      if (v >= 0) {
        from = pos.get(id) ?? 0;
        to = from + v;
        pos.set(id, to);
      } else {
        from = neg.get(id) ?? 0;
        to = from + v;
        neg.set(id, to);
      }
      const yFrom = y(from);
      const yTo = y(to);
      bars.push({
        key: `${s.key}-${i}`,
        seriesKey: s.key,
        color: s.color,
        index: i,
        x: bandX(i) + (band - groupW) / 2 + column * (barW + BAR_GAP),
        y: Math.min(yFrom, yTo),
        w: barW,
        h: Math.abs(yTo - yFrom),
        sign: v > 0 ? "positive" : v < 0 ? "negative" : "zero",
      });
    });
  }

  const labelTicks = bandTicks(
    n,
    band,
    labels.reduce((m, l) => Math.max(m, l.length), 1) * 6,
  );

  return (
    <div
      ref={ref}
      className={cn("relative w-full min-w-0", className)}
      style={{ height }}
    >
      <div
        role="group"
        aria-label={ariaLabel}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onPointerLeave={() => setActive(null)}
        onBlur={() => setActive(null)}
        className={cn("h-full w-full touch-pan-y", focusOutline)}
      >
        <svg
          width="100%"
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          aria-hidden="true"
          focusable="false"
          // Absolute: an svg with a viewBox and a fixed height carries an aspect
          // ratio, which would size its column to the measured width and keep
          // a phone card from ever shrinking it.
          className="absolute inset-0 block overflow-visible"
          data-zero-y={zeroY.toFixed(2)}
        >
          <defs>
            {series.map((s) => (
              <g key={s.key}>
                <linearGradient
                  id={safeId(gradientId, s.key, "pos")}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="0"
                    style={{ stopColor: chartColorVar(s.color) }}
                    stopOpacity={0.28}
                  />
                  <stop
                    offset="1"
                    style={{ stopColor: chartColorVar(s.color) }}
                    stopOpacity={0.06}
                  />
                </linearGradient>
                <linearGradient
                  id={safeId(gradientId, s.key, "neg")}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="0"
                    style={{ stopColor: chartColorVar(s.color) }}
                    stopOpacity={0.06}
                  />
                  <stop
                    offset="1"
                    style={{ stopColor: chartColorVar(s.color) }}
                    stopOpacity={0.28}
                  />
                </linearGradient>
              </g>
            ))}
          </defs>
          {highlightIndex !== null &&
          highlightIndex >= 0 &&
          highlightIndex < n ? (
            <rect
              data-highlight=""
              x={bandX(highlightIndex)}
              y={TOP}
              width={band}
              height={plotH}
              className="fill-muted"
            />
          ) : null}
          <YAxis
            ticks={ticks}
            y={y}
            left={left}
            right={left + plotW}
            gutter={left}
            format={fmtTick}
          />
          <XLabels
            indexes={labelTicks}
            labels={labels}
            x={(i) => bandX(i) + band / 2}
            baseY={height - BOTTOM + 8}
          />
          {bars.map((b) => (
            <g
              key={b.key}
              data-bar=""
              data-bar-sign={b.sign}
              data-series={b.seriesKey}
              data-index={b.index}
              opacity={active !== null && active !== b.index ? 0.55 : 1}
            >
              <rect
                data-bar-wash=""
                x={b.x.toFixed(2)}
                y={b.y.toFixed(2)}
                width={b.w.toFixed(2)}
                height={b.h.toFixed(2)}
                fill={`url(#${safeId(gradientId, b.seriesKey, b.sign === "negative" ? "neg" : "pos")})`}
              />
              {b.h > 0 ? (
                <rect
                  data-bar-cap=""
                  x={b.x.toFixed(2)}
                  y={(b.sign === "negative"
                    ? b.y + b.h - Math.min(2, b.h)
                    : b.y
                  ).toFixed(2)}
                  width={b.w.toFixed(2)}
                  height={Math.min(2, b.h).toFixed(2)}
                  style={{ fill: chartColorVar(b.color) }}
                />
              ) : null}
            </g>
          ))}
          {labels.map((label, i) => (
            <rect
              key={`${i}-${label}`}
              data-band=""
              x={bandX(i)}
              y={TOP}
              width={band}
              height={plotH}
              fill="transparent"
              className={onSelectIndex ? "cursor-pointer" : undefined}
              onPointerEnter={() => setActive(i)}
              onPointerMove={() => setActive(i)}
              onClick={() => onSelectIndex?.(i)}
            />
          ))}
        </svg>
      </div>
      {active !== null ? (
        <ChartTooltip
          anchorX={bandX(active) + band / 2}
          width={width}
          title={labels[active] ?? ""}
          rows={rowsAt(series, active, formatValue)}
        />
      ) : null}
      <ValueTable
        caption={ariaLabel}
        labels={labels}
        series={series}
        formatValue={formatValue}
      />
    </div>
  );
}

type SparklineProps = {
  values: number[];
  color?: ChartColor;
  height?: number;
  ariaLabel: string;
  className?: string;
};

/** A trend with no axes: a 2px line and a dot on the last value. */
function Sparkline({
  values,
  color = "chart-1",
  height = 32,
  ariaLabel,
  className,
}: SparklineProps) {
  const [ref, width] = useMeasuredWidth();
  const pad = 4;
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 0;
  const x = (i: number) =>
    values.length <= 1
      ? width - pad
      : pad + (i / (values.length - 1)) * (width - 2 * pad);
  const y = (v: number) =>
    max === min
      ? height / 2
      : pad + ((max - v) / (max - min)) * (height - 2 * pad);
  const points = values.map((v, i) => ({ x: x(i), y: y(v) }));
  const end = points[points.length - 1];
  return (
    <div
      ref={ref}
      className={cn("relative w-full min-w-0", className)}
      style={{ height }}
    >
      <svg
        role="img"
        aria-label={ariaLabel}
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="absolute inset-0 block"
      >
        <path
          d={linePath(points)}
          fill="none"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ stroke: chartColorVar(color) }}
        />
        {end ? (
          <circle
            cx={end.x}
            cy={end.y}
            r={3}
            style={{ fill: chartColorVar(color) }}
          />
        ) : null}
      </svg>
    </div>
  );
}

type LegendItem = {
  label: string;
  color: ChartColor;
  shape?: "line" | "rect" | "dot";
};

const legendShape = {
  line: "h-0.5 w-3 rounded-full",
  rect: "size-2.5 rounded-[2px]",
  dot: "size-2 rounded-full",
} as const;

/** Small keys that mirror the marks, one row that wraps. */
function ChartLegend({
  items,
  className,
}: {
  items: LegendItem[];
  className?: string;
}) {
  return (
    <ul
      className={cn(
        "m-0 flex list-none flex-wrap items-center gap-x-4 gap-y-1 p-0 text-compact text-muted-foreground",
        className,
      )}
    >
      {items.map((item) => (
        <li key={item.label} className="flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className={cn("shrink-0", legendShape[item.shape ?? "dot"])}
            style={{ background: chartColorVar(item.color) }}
          />
          <span>{item.label}</span>
        </li>
      ))}
    </ul>
  );
}

export { BarChart, ChartLegend, LineChart, Sparkline };
export { chartColorVar } from "./chart-geometry";
export type {
  BarChartProps,
  ChartColor,
  ChartSeries,
  LegendItem,
  LineChartProps,
  SparklineProps,
};
